import { IAMapSetting, IAMapWebServiceSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigAMapSettings } from "../app-settings-config-maps/app-settings-config-amap";
import { ConfigSettingSelect, IConfigSettingOption } from "../config-setting-select";
import { ConfigAMapWebServiceSettings } from "./app-settings-config-amap-web-service";

/** One row of the API-section Amap dropdown. */
interface IAmapCredentialOption extends IConfigSettingOption {
  id: string;
  /** Legacy standalone "Amap Web Service" entries are still resolvable; new ones are not created. */
  isLegacyWebService: boolean;
}

export interface IConfigAMapWebServiceInputState {
  showAmapModal: boolean;
  showLegacyModal: boolean;
  editingAmapId: string;
  editingLegacyId: string;
}

export interface IConfigAMapWebServiceInputProps {
  parentConfigValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

/**
 * Amap credentials for the backend (API) config section. An Amap account's keys live on ONE
 * Amap setting — JS/Android key, security code and the Web Service (REST) key — so the API section
 * selects from the same Amap settings the web and Android sections use; the code generator takes
 * the Web Service Key (or the Api Key) from the selected entry. Standalone "Amap Web Service"
 * entries created before this unification stay listed and resolvable, but new ones are no longer
 * created: "ADD NEW" opens the Amap setting modal.
 */
export class ConfigAMapWebServiceInput extends PureComponent<
  IConfigAMapWebServiceInputProps,
  IConfigAMapWebServiceInputState
> {
  formInputName: string[] = ["apiConfig", "amapWebServiceSettingsId"];

  constructor(props: IConfigAMapWebServiceInputProps) {
    super(props);
    this.state = {
      showAmapModal: false,
      showLegacyModal: false,
      editingAmapId: "",
      editingLegacyId: ""
    };
  }

  openAmapModal = (): void => {
    this.setState({ showAmapModal: true, editingAmapId: "" });
  };

  closeAmapModal = (): void => {
    this.setState({ showAmapModal: false });
  };

  closeLegacyModal = (): void => {
    this.setState({ showLegacyModal: false });
  };

  /** The picker hands back only the id; which modal edits it depends on the list it came from. */
  setEditingSetting = (id: string): void => {
    const option: IAmapCredentialOption | undefined = this.getOptions().find(
      (candidate: IAmapCredentialOption): boolean => candidate.id === id
    );
    if (!option) return;
    if (option.isLegacyWebService) {
      this.setState({ editingLegacyId: option.id, showLegacyModal: true });
      return;
    }
    this.setState({ editingAmapId: option.id, showAmapModal: true });
  };

  handleRestore = (): void => {
    this.props.handleUpdateCustomFormInput?.(this.formInputName, this.props.parentConfigValue ?? null);
  };

  private getOptions(): IAmapCredentialOption[] {
    const amapSettings: IAMapSetting[] = this.props.commonSettings.amapSettings ?? [];
    const legacy: IAMapWebServiceSetting[] = this.props.commonSettings.amapWebServiceSettings ?? [];
    const amapIds: Set<string> = new Set(amapSettings.map((setting: IAMapSetting): string => setting.id));
    return [
      ...amapSettings.map((setting: IAMapSetting): IAmapCredentialOption => ({
        id: setting.id,
        name: setting.name,
        isGlobalCommonConfigOnProjectConfig: setting.isGlobalCommonConfigOnProjectConfig,
        isLegacyWebService: false
      })),
      ...legacy
        .filter((setting: IAMapWebServiceSetting): boolean => !amapIds.has(setting.id))
        .map((setting: IAMapWebServiceSetting): IAmapCredentialOption => ({
          id: setting.id,
          name: `${setting.name} (legacy web service key)`,
          isGlobalCommonConfigOnProjectConfig: setting.isGlobalCommonConfigOnProjectConfig,
          isLegacyWebService: true
        }))
    ];
  }

  render(): ReactNode {
    return (
      <>
        <ConfigAMapSettings
          formInputName={this.formInputName}
          closeModal={this.closeAmapModal}
          showModal={this.state.showAmapModal}
          editingMapsId={this.state.editingAmapId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
        <ConfigAMapWebServiceSettings
          formInputName={this.formInputName}
          closeModal={this.closeLegacyModal}
          showModal={this.state.showLegacyModal}
          editingAMapWebServiceId={this.state.editingLegacyId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
        <ConfigSettingSelect
          label="Amap"
          formInputName={this.formInputName}
          options={this.getOptions()}
          placeholder="Choose Amap setting"
          allowClear
          tooltipContentKey="appsettings_mcp_amapwebservice"
          onAddNew={this.openAmapModal}
          onEdit={this.setEditingSetting}
          parentConfigValue={this.props.parentConfigValue}
          onRestore={this.handleRestore}
          dropdownTestId="appSettingsMcpAMapWebServiceDropdown"
          addNewTestId="appSettingsMcpAMapWebServiceAddNewButton"
          editTestId="appSettingsMcpAMapWebServiceEditButton"
        />
      </>
    );
  }
}
